import { Method } from 'axios';

import { sendRequest } from '@edirect/request';
import {
  AuthServiceInterface,
  TokenSetType,
} from '../auth';
import { oidcService } from '../auth/oidc.service';

export class AuthService implements AuthServiceInterface {
  public sendServerRequest(
    method: Method,
    uri: string,
    data?: unknown,
  ): Promise<void | unknown> {
    return this.getClientToken().then(({ accessToken }) =>
      sendRequest('auth-service', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).request(method, uri, data),
    );
  }

  public sendUserRequest(
    tokenSet: TokenSetType,
    method: Method,
    uri: string,
    data?: unknown,
  ): Promise<Record<string, unknown>> {
    return sendRequest('auth-service', {
      headers: {
        Authorization: `${tokenSet.tokenType} ${tokenSet.accessToken}`,
      },
    }).request(method, uri, data) as Promise<Record<string, unknown>>;
  }

  public createUser(data: unknown): Promise<void | unknown> {
    return this.sendServerRequest('POST', '/user', data);
  }

  public createUserExternal(data: unknown): Promise<void | unknown> {
    return this.sendServerRequest('POST', '/user/external', data);
  }

  public updateUser(id: string, data: unknown): Promise<void | unknown> {
    return this.sendServerRequest('PUT', `/user/${id}`, data);
  }

  public sendConfirmationEmail(data: unknown): Promise<void | unknown> {
    return this.sendServerRequest('POST', '/user/confirm', data);
  }

  public getUser(id: string): Promise<void | unknown> {
    return this.sendServerRequest('GET', `/user/${id}`);
  }

  public getUsers(query = {}): Promise<void | unknown> {
    return this.sendServerRequest('GET', `/user?${new URLSearchParams(query)}`);
  }

  public newRecoverPassword(data: unknown): Promise<void | unknown> {
    return this.sendServerRequest('PATCH', '/user/recover-password', data);
  }

  public setNewPassword(
    user: string,
    token: string,
    data: unknown,
  ): Promise<void | unknown> {
    return this.sendServerRequest(
      'PUT',
      `/user/recover-password/${user}/${token}`,
      data,
    );
  }

  public async getClientToken(): Promise<TokenSetType> {
    const client = await oidcService.getServerClient();

    return client.getAccessToken({
      scope: 'openid accountId username status roles permissions entityUser',
    });
  }

  public async getAccessToken(
    username: string,
    password?: string,
    passwordPlainText?: string,
  ): Promise<TokenSetType> {
    const client = await oidcService.getUserClient();

    return client.getAccessToken({
      scope: 'openid accountId username status roles permissions entityUser',
      username,
      password,
      passwordPlainText,
    });
  }

  public async getRefreshToken(tokenSet: TokenSetType): Promise<void> {
    const client = await oidcService.getUserClient();

    return client.getRefreshToken(tokenSet);
  }

  public getAccount(tokenSet: TokenSetType): Promise<Record<string, unknown>> {
    return this.sendUserRequest(tokenSet, 'GET', `/oidc/me`).then((result) => {
      const resultObject = result as Record<string, unknown>;
      const oauth = {
        ...resultObject,
        user: resultObject.entityUser,
      } as Record<string, unknown>;
      delete oauth.entityUser;
      return oauth;
    });
  }

  public validateToken(jwt: JsonWebKey): Promise<void | unknown> {
    return this.sendServerRequest('POST', '/token/validate', { jwt });
  }
}

export const authService = new AuthService();
